/**
 * Utility functions for accessing shared files stored by Service Worker
 */

const SHARED_FILES_DB = 'family-album-shared-files';
const SHARED_FILES_STORE = 'files';

interface SharedFileData {
  id: string;
  name: string;
  type: string;
  size: number;
  data: ArrayBuffer;
  timestamp: number;
}

function openSharedFilesDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(SHARED_FILES_DB, 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(SHARED_FILES_STORE)) {
        db.createObjectStore(SHARED_FILES_STORE, { keyPath: 'id' });
      }
    };
  });
}

export async function getSharedFile(fileId: string): Promise<File | null> {
  try {
    const db = await openSharedFilesDB();
    const transaction = db.transaction([SHARED_FILES_STORE], 'readonly');
    const store = transaction.objectStore(SHARED_FILES_STORE);
    
    const fileData: SharedFileData | undefined = await new Promise((resolve, reject) => {
      const request = store.get(fileId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (!fileData) {
      console.warn('[SharedFiles] File not found:', fileId);
      return null;
    }
    
    // Convert ArrayBuffer back to File
    const blob = new Blob([fileData.data], { type: fileData.type });
    const file = new File([blob], fileData.name, { 
      type: fileData.type,
      lastModified: fileData.timestamp 
    });
    
    return file;
  } catch (error) {
    console.error('[SharedFiles] Error retrieving file:', error);
    return null;
  }
}

export async function getSharedFiles(fileIds: string[]): Promise<File[]> {
  const files: File[] = [];
  
  for (const id of fileIds) {
    const file = await getSharedFile(id);
    if (file) {
      files.push(file);
    }
  }
  
  return files;
}

export async function deleteSharedFile(fileId: string): Promise<void> {
  try {
    const db = await openSharedFilesDB();
    const transaction = db.transaction([SHARED_FILES_STORE], 'readwrite');
    const store = transaction.objectStore(SHARED_FILES_STORE);
    
    await new Promise<void>((resolve, reject) => {
      const request = store.delete(fileId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    console.log('[SharedFiles] Deleted file:', fileId);
  } catch (error) {
    console.error('[SharedFiles] Error deleting file:', error);
  }
}

export async function deleteSharedFiles(fileIds: string[]): Promise<void> {
  for (const id of fileIds) {
    await deleteSharedFile(id);
  }
}

